import {
  BusinessClient,
  BusinessService,
  BusinessUser,
  MetricsCountService,
  PlanLimitsService,
  ProductService,
  SubscriptionService,
  TargetAudienceService,
  UserPlanCount,
} from '../index'
import { getBusinessByClientId } from '../business'
import { getUserAccountFirstOppenMyPost } from '../userAccounts'
import { MIN_ARTS_TO_CREATE_CAMPAIGN } from '../../constants/campaignCreation'
import { getArtGenerationAvailability } from '../../utils/artConsumption'

/**
 * NavigationValidationService
 *
 * Logica pura responsavel por validar se um usuario pode navegar
 * para uma rota de publicacao. Retorna resultados estruturados que
 * o hook/componente consumidor usa para decidir o que mostrar (toasts,
 * modals) e se deve prosseguir com a navegacao.
 *
 * No React, sem estado, sem UI — apenas funções assíncronas que retornam dados.
 */

// ── Helpers ──────────────────────────────────────────────────────────

const getPlanItemQuantity = (userSubscription, itemType) => {
  const planItems = userSubscription?.data?.[0]?.Plan?.PlanItems

  if (!Array.isArray(planItems)) {
    return null
  }

  const planItem = planItems.find((item) => item.type === itemType)
  return planItem?.quantity ?? null
}

const verifyUserNiche = async (clientData) => {
  const getUserNiche = async () => {
    if (clientData?.uuid) {
      const { data } = await getBusinessByClientId(clientData.uuid)
      const businessNiche = data[0]?.BusinessNiche?.name
      return Boolean(businessNiche)
    }

    const { data } = await BusinessService.getBusinessByLogin()
    return data.length > 0
  }

  return await getUserNiche()
}

const getAvailableArtsCount = (userSubscriptionFeatures, userPlanCountData) => {
  const totalArts =
    Number(userSubscriptionFeatures?.data?.singlePostLimit ?? 0) +
    Number(userSubscriptionFeatures?.data?.additionalSinglePostQuantity ?? 0)
  const usedArts = Number(
    userSubscriptionFeatures?.data?.artGenerationCount ??
    userPlanCountData?.artGenerationCount ??
    0
  )

  return Math.max(0, totalArts - usedArts)
}

const checkDowngradeBlock = async (userSubscription) => {
  let blocked = false

  const productCount = await ProductService.getAllproductCount()

  if (userSubscription.data.length <= 0 && productCount.data.count > 30) {
    return true
  } else if (userSubscription.data.length > 0) {
    const quantityProduct = getPlanItemQuantity(userSubscription, 'product_quantity')

    if (productCount.data.count > quantityProduct && quantityProduct != null) {
      return true
    }
  }

  const businessTargetAudienceCount =
    await TargetAudienceService.getAllTargetAudienceCount()

  if (
    userSubscription.data.length <= 0 &&
    businessTargetAudienceCount.data.count > 30
  ) {
    return true
  } else if (userSubscription.data.length > 0) {
    const quantityTargetAudience = getPlanItemQuantity(
      userSubscription,
      'target_audience_quantity'
    )

    if (
      businessTargetAudienceCount.data.count > quantityTargetAudience &&
      quantityTargetAudience != null
    ) {
      return true
    }
  }

  const businessUserCount = await BusinessUser.getAllBusinessUserCount()
  if (userSubscription.data.length <= 0 && businessUserCount.data.count > 1) {
    return true
  } else if (userSubscription.data.length > 0) {
    const quantityItemUser = getPlanItemQuantity(userSubscription, 'user_quantity')

    if (
      businessUserCount.data.count > quantityItemUser &&
      quantityItemUser != null
    ) {
      return true
    }
  }

  const businessClientCount = await BusinessClient.getAllBusinessClientCount()

  if (
    userSubscription.data.length <= 0 &&
    businessClientCount.data.count > 1
  ) {
    return true
  } else if (userSubscription.data.length > 0) {
    const quantityClient = getPlanItemQuantity(userSubscription, 'client_quantity')

    if (
      businessClientCount.data.count > quantityClient &&
      quantityClient != null
    ) {
      return true
    }
  }

  return blocked
}

const ensureUserPlanCount = async (userSubscription) => {
  const userPlanCount = await UserPlanCount.getUserPlanCountByUser()
  const userPlanCountData = Array.isArray(userPlanCount?.data)
    ? userPlanCount.data[0]
    : userPlanCount?.data
  const userPlanCountId = userPlanCountData?.uuid ?? userPlanCountData?.id
  const subscriptionData = Array.isArray(userSubscription?.data)
    ? userSubscription.data
    : []
  const mesAtual = new Date(new Date().setDate(1))
  mesAtual.setDate(mesAtual.getDate() + 30)
  mesAtual.setHours(0, 0, 0, 0)

  if (!userPlanCountData) {
    await UserPlanCount.postUserPlanCount({
      ownScheduleCount: 0,
      postOnDesignCount: 0,
      imageBankCount: 0,
      aiImageCount: 0,
      aiLegendCount: 0,
      aiMessageCount: 0,
      renewalDate:
        subscriptionData.length > 0
          ? subscriptionData[0].expiration
          : mesAtual,
    })

    const refreshed = await UserPlanCount.getUserPlanCountByUser()
    return Array.isArray(refreshed?.data) ? refreshed.data[0] : refreshed?.data
  }

  const renewalDate = new Date(userPlanCountData.renewalDate)
  const dataAtual = new Date()
  const expirationPlan =
    subscriptionData.length > 0
      ? new Date(subscriptionData[0].expiration)
      : new Date(mesAtual)

  const renewalDateStr = new Date(userPlanCountData.renewalDate)
    .toISOString()
    .split('T')[0]
  const expirationPlanStr = expirationPlan.toISOString().split('T')[0]

  if (
    renewalDate.getTime() < dataAtual.getTime() ||
    renewalDateStr !== expirationPlanStr
  ) {
    if (userPlanCountId) {
      await UserPlanCount.putUserPlanCount(userPlanCountId, {
        ownScheduleCount: 0,
        postOnDesignCount: 0,
        imageBankCount: 0,
        aiImageCount: 0,
        aiLegendCount: 0,
        aiMessageCount: 0,
        artGenerationCount: 0,
        renewalDate:
          subscriptionData.length > 0
            ? subscriptionData[0].expiration
            : mesAtual,
      })
    }
    const refreshed = await UserPlanCount.getUserPlanCountByUser()
    return Array.isArray(refreshed?.data) ? refreshed.data[0] : refreshed?.data
  }

  return userPlanCountData
}

const trackNavigationMetric = async (route) => {
  const metricsCount = await MetricsCountService.getMetricsCountTodayByUser()

  if (metricsCount.data) {
    const dataMetrics = {
      ownScheduleCount:
        route === '/NewPublicationPageMyPublication'
          ? metricsCount.data.ownScheduleCount + 1
          : metricsCount.data.ownScheduleCount,
      postOnDesignCount:
        route === '/ChatSocket'
          ? metricsCount.data.postOnDesignCount + 1
          : metricsCount.data.postOnDesignCount,
      campaignCount:
        route === '/NewPublicationPageCampaign'
          ? metricsCount.data.campaignCount + 1
          : metricsCount.data.campaignCount,
      calendarCount: metricsCount.data.calendarCount,
    }

    await MetricsCountService.putMetricsCount(metricsCount.data.uuid, dataMetrics)
  } else {
    const dataMetrics = {
      ownScheduleCount: route === '/NewPublicationPageMyPublication' ? 1 : 0,
      postOnDesignCount: route === '/ChatSocket' ? 1 : 0,
      campaignCount: route === '/NewPublicationPageCampaign' ? 1 : 0,
      calendarCount: 0,
    }

    await MetricsCountService.postMetricsCount(dataMetrics)
  }
}

// ── Route-specific validators ─────────────────────────────────────────

/**
 * Validate navigation to /ChatSocket (Campaign with IA)
 */
const validateCampaignRoute = (userSubscriptionFeatures, userPlanCountData) => {
  const availableArts = getAvailableArtsCount(
    userSubscriptionFeatures,
    userPlanCountData
  )

  if (availableArts < MIN_ARTS_TO_CREATE_CAMPAIGN) {
    return {
      canNavigate: false,
      reason: 'art_generation_limit_reached',
      message: `Você precisa de pelo menos ${MIN_ARTS_TO_CREATE_CAMPAIGN} artes disponíveis para criar uma campanha.`,
    }
  }

  return { canNavigate: true }
}

/**
 * Validate navigation to /NewPublicationPageIA (Art generation with IA)
 */
const validateArtGenerationRoute = async () => {
  const planLimitResponse = await PlanLimitsService.checkSinglePostCountLimit()
  const hasAvailableArts =
    planLimitResponse?.responseStatus === 200 &&
      typeof planLimitResponse?.data === 'boolean'
      ? planLimitResponse.data
      : null

  if (hasAvailableArts === false) {
    return {
      canNavigate: false,
      reason: 'art_limit_reached',
      messageKey: 'plans.user_plan_count_ai_image',
    }
  }

  return { canNavigate: true }
}

/**
 * Validate navigation to /NewPublicationPageMyPublication (Schedule image)
 */
const validateSchedulingRoute = async () => {
  return { canNavigate: true }
}

// ── Main validation entry point ───────────────────────────────────────

/**
 * Validates navigation for a given route. Returns a structured result
 * that tells the caller whether to proceed, show a toast, or show a modal.
 *
 * @param {string} route
 * @param {object} options
 * @param {object} options.clientData
 * @returns {Promise<{
 *   canNavigate: boolean,
 *   reason?: string,
 *   message?: string,
 *   messageKey?: string,
 *   showDowngradeModal?: boolean,
 *   showPlanLimitToast?: boolean,
 *   planLimitToastMessage?: string,
 *   planLimitToastMessageKey?: string,
 *   navigationState?: object,
 * }>}
 */
export const validateNavigation = async (route, { clientData } = {}) => {
  if (route === '/NewPublicationPageIA') {
    const result = await validateArtGenerationRoute()
    if (!result.canNavigate) return result
  }

  const userSubscription = await SubscriptionService.getSubscriptionByUser()
  const userSubscriptionFeatures =
    await SubscriptionService.getSubscriptionFeatures()
  const isDowngradeBlocked = await checkDowngradeBlock(userSubscription)

  if (isDowngradeBlocked) {
    return {
      canNavigate: false,
      reason: 'downgrade_blocked',
      showDowngradeModal: true,
    }
  }

  const userPlanCountData = await ensureUserPlanCount(userSubscription)

  if (route === '/ChatSocket') {
    const result = validateCampaignRoute(
      userSubscriptionFeatures,
      userPlanCountData
    )
    if (!result.canNavigate) return result
  }

  if (route === '/NewPublicationPageMyPublication') {
    if (userSubscriptionFeatures?.data?.unlimitedScheduling === true) {
      return { canNavigate: true }
    }
    if (
      userSubscription.data.length <= 0 &&
      userPlanCountData?.ownScheduleCount >= 10
    ) {
      return {
        canNavigate: false,
        reason: 'scheduling_limit_reached',
        showPlanLimitToast: true,
        planLimitToastMessageKey: 'plans.user_plan_count_post_design',
      }
    }

    // Paid plan scheduling limit
    if (userSubscription.data.length > 0) {
      const quantityScheduling = getPlanItemQuantity(userSubscription, 'scheduling_quantity')

      if (
        userPlanCountData?.ownScheduleCount >= quantityScheduling &&
        quantityScheduling != null
      ) {
        return {
          canNavigate: false,
          reason: 'scheduling_limit_reached',
          showPlanLimitToast: true,
          planLimitToastMessageKey: 'plans.user_plan_count_post_design',
        }
      }
    }
  }

  if (route === '/NewPublicationPageIA') {
    const { hasAvailableArts } = await getArtGenerationAvailability(1)

    if (!hasAvailableArts) {
      return {
        canNavigate: false,
        reason: 'art_generation_limit_reached',
        showPlanLimitToast: true,
        planLimitToastMessageKey: 'plans.user_plan_count_post_design',
      }
    }
  }

  await trackNavigationMetric(route)

  if (route === '/NewPublicationPageMyPublication') {
    const firstOppenMyPost = await getUserAccountFirstOppenMyPost()

    if (!firstOppenMyPost.data.firstOppenMyPost) {
      return {
        canNavigate: true,
        navigationState: { tutorialMovie: true },
      }
    }
  }

  return { canNavigate: true }
}

export const NavigationValidationService = {
  validateNavigation,
  validateCampaignRoute,
  validateArtGenerationRoute,
  validateSchedulingRoute,
  checkDowngradeBlock,
  verifyUserNiche,
}
